import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';
import axios from 'axios';
import BoxHolder from '../../components/shared/BoxHolder/BoxHolder';
import Badge from '../../components/shared/Badge/Badge';
import Loading from '../../components/shared/Loading';
import BtButton from '../bt-button/bt-button';
import {
  ManageResult,
  RequestStatus,
  serviceEvaluations,
} from '../../services/service-evaluations';
import {
  EvaluationRecipientStatus,
  EvaluationRequestStatus,
  EvaluationSource,
} from '../../util/evaluationContract';
import { PROGRESS_BAR, requestStatusMeta } from '../../util/evaluationStatusMeta';
import EvaluationsLayout from './EvaluationsLayout';
import StatusTiles from './StatusTiles';
import RecipientTable, { RecipientRow, RowActions } from './RecipientTable';
import ResponseModal from './ResponseModal';
import RequestActions from './RequestActions';
import { ChangeEmailModal, RevokeRecipientModal } from './RecipientModals';
import { copyText, notifySend, reportActionError } from './manageActions';
import TagChips from './TagChips';
import { loadPage } from './fullPageLoad';
import { formatDayKey } from './reviewFormat';
import { reportUnexpected } from './reportUnexpected';
import styles from './page-supplier-evaluations.module.scss';

type Props = {
  requestId: string;
  registerUrl: string;
  reviewUrlOf: (supplierId: string) => string;
};

type LoadFailure = 'notFound' | 'failed';

const POLL_INTERVAL_MS = 5000;
const POLL_WINDOW_MS = 2 * 60 * 1000;
const MAX_POLLS = 24;

const mergeRecipients = (current: RecipientRow[], incoming: RecipientRow[]): RecipientRow[] => {
  const incomingById = new Map(incoming.map(recipient => [recipient._id, recipient]));
  const known = new Set(current.map(recipient => recipient._id));
  return [
    ...current.map(recipient => incomingById.get(recipient._id) ?? recipient),
    ...incoming.filter(recipient => !known.has(recipient._id)),
  ];
};

const TERMINAL: string[] = [EvaluationRequestStatus.Closed, EvaluationRequestStatus.Cancelled];

const isStillSending = (status: RequestStatus) =>
  !TERMINAL.includes(status.request.status) &&
  status.recipients.some(recipient => recipient.status === EvaluationRecipientStatus.NotSent) &&
  Date.now() - Date.parse(status.request.createdAt) < POLL_WINDOW_MS;

const RequestStatusPage: FC<Props> = ({ requestId, registerUrl, reviewUrlOf }) => {
  const [t, i18n] = useTranslation('evaluations');
  const history = useHistory();
  const [status, setStatus] = useState<RequestStatus | null>(null);
  const [failure, setFailure] = useState<LoadFailure | null>(null);
  const [openResponseId, setOpenResponseId] = useState<string | null>(null);
  const [emailRecipient, setEmailRecipient] = useState<RecipientRow | null>(null);
  const [revokedRecipient, setRevokedRecipient] = useState<RecipientRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [answeringId, setAnsweringId] = useState<string | null>(null);
  const polls = useRef(0);
  const actionSeq = useRef(0);

  const fetchStatus = useCallback(async () => {
    const startedAt = actionSeq.current;
    const next = await serviceEvaluations.getRequestStatus(requestId);
    // A read that started before an action resolved would undo what the action returned.
    if (startedAt !== actionSeq.current) return;
    setStatus(previous =>
      previous
        ? { ...next, recipients: mergeRecipients(previous.recipients, next.recipients) }
        : next,
    );
  }, [requestId]);

  const load = useCallback(async () => {
    try {
      await fetchStatus();
      setFailure(null);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        setFailure('notFound');
        return;
      }
      reportUnexpected(error);
      setFailure('failed');
    }
  }, [fetchStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const applyResult = useCallback((result: ManageResult) => {
    actionSeq.current += 1;
    setStatus(result.status);
  }, []);

  const rowActions: RowActions | null = useMemo(() => {
    if (!status?.canManage) return null;
    const resend = async (recipient: RecipientRow) => {
      if (busyId) return;
      setBusyId(recipient._id);
      try {
        const result = await serviceEvaluations.resendRecipient(requestId, recipient._id);
        applyResult(result);
        if (result.outcome)
          notifySend(t, result.outcome, t('status.row.resent', 'Utvärderingen skickades igen'));
      } catch (error) {
        reportActionError(t, error, load);
      } finally {
        setBusyId(null);
      }
    };
    const copyLink = async (recipient: RecipientRow) => {
      const pending = serviceEvaluations.copyRecipientLink(requestId, recipient._id);
      const copied = copyText(pending.then(({ link }) => link));
      copied.catch(() => undefined);
      try {
        await pending;
      } catch (error) {
        reportActionError(t, error, load);
        return;
      }
      try {
        await copied;
        NotificationManager.success(t('status.row.linkCopied', 'Länken är kopierad'));
      } catch {
        NotificationManager.error(t('status.row.copyFailed', 'Det gick inte att kopiera länken'));
      }
    };
    return {
      busyId,
      onResend: resend,
      onCopyLink: copyLink,
      onChangeEmail: setEmailRecipient,
      onRevoke: setRevokedRecipient,
    };
  }, [status?.canManage, busyId, requestId, applyResult, load, t]);

  const answerNow = useCallback(
    async (recipient: RecipientRow) => {
      if (answeringId) return;
      setAnsweringId(recipient._id);
      try {
        const { path } = await serviceEvaluations.getAnswerPath(requestId, recipient._id);
        loadPage(`${path}?back=${encodeURIComponent(history.location.pathname)}`);
      } catch (error) {
        reportActionError(t, error, load);
        setAnsweringId(null);
      }
    },
    [answeringId, requestId, history, load, t],
  );

  useEffect(() => {
    if (!status || polls.current >= MAX_POLLS || !isStillSending(status)) return undefined;
    const timer = window.setTimeout(() => {
      polls.current += 1;
      fetchStatus().catch(reportUnexpected);
    }, POLL_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [status, fetchStatus]);

  const retry = () => {
    setFailure(null);
    setStatus(null);
    load();
  };

  const request = status?.request;
  const path = [request?.supplier.name, request?.title].filter((segment): segment is string =>
    Boolean(segment),
  );
  const backTo = request ? reviewUrlOf(request.supplier._id) : registerUrl;

  const renderContent = () => {
    if (failure === 'notFound') {
      return (
        <p className={styles.loadError} role="alert">
          {t('status.notFound', 'Utvärderingen hittades inte.')}
        </p>
      );
    }
    if (failure) {
      return (
        <div className={styles.loadError} role="alert">
          <span>{t('status.loadFailed', 'Utvärderingen kunde inte hämtas.')}</span>
          <BtButton size="xxs" tabIndex={0} onClick={retry}>
            {t('review.retry', 'Försök igen')}
          </BtButton>
        </div>
      );
    }
    if (!status || !request) return <Loading type="inline" />;

    const { context, supplier } = request;
    const { recipients, answered } = status.tiles;
    const project = [context.projectNumber, context.projectName].filter(Boolean).join(' ');
    const contextLabel =
      [project, context.areaName].filter(Boolean).join(' · ') ||
      t('status.context.none', 'Utan projekt');
    const requestMeta = requestStatusMeta(request.status);
    const imported = request.source === EvaluationSource.Legacy;
    const origin = imported
      ? t('status.meta.imported', 'Importerad från Erfarenhetsåterföring')
      : request.senderName &&
        t('status.meta.createdBy', 'Skapad av {{name}}', { name: request.senderName });
    const readOnly = !status.canManage && !TERMINAL.includes(request.status);
    const revoked = status.recipients.filter(
      recipient => recipient.status === EvaluationRecipientStatus.Revoked,
    ).length;

    return (
      <>
        <BoxHolder className={styles.card}>
          <div className={styles.requestHeader}>
            <div className={styles.requestHeaderText}>
              <div className={styles.titleRow}>
                <h2 className={styles.entityTitle}>{request.title}</h2>
                <Badge color={requestMeta.color} backgroundColor={requestMeta.backgroundColor}>
                  {t(requestMeta.labelKey, requestMeta.fallback)}
                </Badge>
              </div>
              <p className={`${styles.metaLine} ${styles.muted}`}>
                {[
                  contextLabel,
                  t('status.dueDate', 'Sista svarsdag {{date}}', {
                    date: formatDayKey(request.dueDate, i18n.language),
                  }),
                  origin,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              <TagChips tags={request.tags} />
              {recipients > 0 && (
                <div className={styles.progress}>
                  <div className={styles.progressTrack} style={{ background: PROGRESS_BAR.track }}>
                    <div
                      className={styles.progressFill}
                      style={{
                        background: PROGRESS_BAR.fill,
                        width: `${Math.min(100, (answered / recipients) * 100)}%`,
                      }}
                    />
                  </div>
                  <span className={`${styles.small} ${styles.muted}`}>
                    {t('status.answeredOf', '{{answered}} av {{total}} har svarat', {
                      answered,
                      total: recipients,
                    })}
                  </span>
                </div>
              )}
              {request.status === EvaluationRequestStatus.Closed && (
                <p className={`${styles.small} ${styles.muted} ${styles.stateNotice}`}>
                  {t('status.closedNotice', 'Utvärderingen är stängd. Inga fler svar tas emot.')}
                </p>
              )}
              {request.status === EvaluationRequestStatus.Cancelled && (
                <p className={`${styles.small} ${styles.muted} ${styles.stateNotice}`}>
                  {t(
                    'status.cancelledNotice',
                    'Utvärderingen är avbruten. Svaren räknas inte i betyget.',
                  )}
                </p>
              )}
              {readOnly && (
                <p className={`${styles.small} ${styles.muted} ${styles.stateNotice}`}>
                  {request.senderName && !imported
                    ? t(
                        'status.readOnly',
                        'Du kan se utvärderingen, men bara {{name}} och administratörer kan ändra den.',
                        { name: request.senderName },
                      )
                    : t(
                        'status.readOnlyAdmin',
                        'Du kan se utvärderingen, men bara administratörer kan ändra den.',
                      )}
                </p>
              )}
            </div>
            <RequestActions
              status={status}
              onResult={applyResult}
              onStale={load}
              onOpenProfile={() =>
                history.push(registerUrl, { q: supplier.organizationNumber || supplier.name })
              }
            />
          </div>
        </BoxHolder>
        <StatusTiles tiles={status.tiles} requestStatus={request.status} />
        <BoxHolder className={styles.card}>
          <h3 className={styles.cardTitle}>{t('status.recipients.title', 'Mottagare')}</h3>
          {revoked > 0 && (
            <p className={`${styles.small} ${styles.muted} ${styles.cardNote}`}>
              {t('status.recipients.revokedNote', {
                count: revoked,
                defaultValue_one: '{{count}} återkallad mottagare räknas inte i rutorna ovan.',
                defaultValue_other: '{{count}} återkallade mottagare räknas inte i rutorna ovan.',
              })}
            </p>
          )}
          <div className={styles.table}>
            <RecipientTable
              recipients={status.recipients}
              onShowAnswer={setOpenResponseId}
              onAnswerNow={answerNow}
              answeringId={answeringId}
              actions={rowActions}
            />
          </div>
        </BoxHolder>
        {openResponseId && (
          <ResponseModal
            responseId={openResponseId}
            onClose={() => setOpenResponseId(null)}
            onHidden={load}
          />
        )}
        {emailRecipient && (
          <ChangeEmailModal
            requestId={requestId}
            recipient={emailRecipient}
            onResult={applyResult}
            onStale={load}
            onClose={() => setEmailRecipient(null)}
          />
        )}
        {revokedRecipient && (
          <RevokeRecipientModal
            requestId={requestId}
            recipient={revokedRecipient}
            onResult={applyResult}
            onStale={load}
            onClose={() => setRevokedRecipient(null)}
          />
        )}
      </>
    );
  };

  return (
    <EvaluationsLayout path={path} backTo={backTo}>
      {renderContent()}
    </EvaluationsLayout>
  );
};

export default RequestStatusPage;
